/**
 * `/exam-prep` (#440, UX §4.7b): the plan for one assessment, shown once, and the way into its sessions.
 *
 * The numbers here (topics, sessions, minutes) are the size of the plan, never a measure of what the learner knows. The
 * course focus filters which assessments are listed; it never changes what is recorded. Which topics are listed and in what
 * tier is the server's ranking; this screen only shows it and lets the learner reorder it. Titles, section names, sheet and
 * question labels are untrusted text.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { ExamPrepPlanResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useCourseFocus } from '../../app/course-focus.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useLocale } from '../../i18n/locale.js';
import { courseShortName as titleOf } from '../../app/course-names.js';
import { UntrustedText } from '../homework/untrusted-text.js';
import { assessmentDate, useAssessmentLabels } from '../setup/assessment-labels.js';
import { usePrep, type Unavailable } from './prep-labels.js';

type PlanTopic = ExamPrepPlanResponse['topics'][number];

export function ReasonLine({ sheet, question }: { readonly sheet: string | null; readonly question: string | null }): React.JSX.Element | null {
  if (sheet === null && question === null) return null;
  return (
    <bdi>
      {sheet !== null ? <UntrustedText text={sheet} /> : null}
      {sheet !== null && question !== null ? ', ' : null}
      {question !== null ? <UntrustedText text={question} /> : null}
    </bdi>
  );
}

function TopicName({ topic }: { readonly topic: { readonly sectionName: string | null } }): React.JSX.Element {
  const t = usePrep();
  return topic.sectionName !== null ? <UntrustedText text={topic.sectionName} /> : <>{t.topicFallback}</>;
}

function Because({ topic }: { readonly topic: PlanTopic }): React.JSX.Element {
  const t = usePrep();
  const entries = topic.because;
  return (
    <span className="prep-muted prep-because">
      {entries.length === 0 ? (
        t.fromSemester
      ) : (
        <>
          {t.from}{' '}
          {entries.map((entry, index) => (
            <span key={index}>
              {index > 0 ? '; ' : null}
              <ReasonLine sheet={entry.sheet} question={entry.question} />
            </span>
          ))}
        </>
      )}
    </span>
  );
}

function PlanView({ plan }: { readonly plan: ExamPrepPlanResponse }): React.JSX.Element {
  const t = usePrep();
  const api = useApi();
  const { locale } = useLocale();
  const { focused } = useCourseFocus();
  const kinds = useAssessmentLabels();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [unavailable, setUnavailable] = useState<Unavailable | null>(null);

  const reorder = useMutation({
    mutationFn: (topicKeys: string[]) => api.reorderExamPrepTopics(plan.planId, crypto.randomUUID(), { topicKeys }),
    onSuccess: (updated) => queryClient.setQueryData(queryKeys.examPrepPlan(plan.planId), updated),
  });
  const start = useMutation({
    mutationFn: () => api.startExamPrepSession(plan.planId, crypto.randomUUID()),
    onSuccess: (response) => {
      if (response.session !== null) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.examPrepPlan(plan.planId) });
        void navigate(`/session/${encodeURIComponent(response.session.sessionId)}`);
      } else {
        const why = response.unavailable;
        setUnavailable(why === 'assessment_passed' || why === 'plan_has_no_problems' || why === 'nothing_due_yet' ? why : 'nothing_due_yet');
      }
    },
  });

  const move = (index: number, by: -1 | 1): void => {
    const keys = plan.topics.map((topic) => topic.topicKey);
    const target = index + by;
    const moved = keys[index];
    const other = keys[target];
    if (moved === undefined || other === undefined) return;
    keys[index] = other;
    keys[target] = moved;
    reorder.mutate(keys);
  };

  const courseName = focused === null ? '' : titleOf(focused, locale);
  const date = assessmentDate(plan.assessment.date, locale);

  if (plan.status === 'assessment_passed') return <p>{t.passed}</p>;

  const startControl =
    plan.sessions.openSessionId !== null ? (
      <Link className="prep-pill prep-hero-action" to={`/session/${encodeURIComponent(plan.sessions.openSessionId)}`}>
        {t.continue}
      </Link>
    ) : (
      <button type="button" className="prep-pill prep-hero-action" disabled={start.isPending} onClick={() => start.mutate()}>
        {t.start}
      </button>
    );

  return (
    <section className="prep-plan" aria-labelledby="prep-plan-title">
      {plan.status === 'ready' ? (
        <div className="prep-hero">
          <div className="prep-hero-text">
            <p className="prep-eyebrow" dir="auto">
              {t.eyebrow(courseName, kinds.kind(plan.assessment.kind), date)}
            </p>
            <h2 id="prep-plan-title">{t.topicsToWork(plan.topics.length)}</h2>
            <p className="prep-hero-line">{t.aboutSessions(plan.sessions.aboutCount, plan.sessions.minutes)}</p>
            <div className="prep-dots-row">
              <ol className="prep-dots" aria-label={t.sessionsLabel}>
                {Array.from({ length: plan.sessions.aboutCount }, (_, index) => (
                  <li key={index} className="prep-dot" data-done={index < plan.sessions.done}>
                    <span className="visually-hidden">
                      {index < plan.sessions.done ? t.sessionDone(index + 1) : t.sessionToDo(index + 1)}
                    </span>
                  </li>
                ))}
              </ol>
              <span className="prep-dots-label">{t.sessionCount(plan.sessions.aboutCount)}</span>
            </div>
          </div>
          {startControl}
        </div>
      ) : (
        <>
          <h2 id="prep-plan-title" className="visually-hidden">
            <UntrustedText text={plan.assessment.title} />
          </h2>
          <p>{t.noCheckable}</p>
        </>
      )}

      {plan.topics.length > 0 ? (
        <>
          <h2>{t.topicsHeading}</h2>
          <ol className="prep-topics">
            {plan.topics.map((topic, index) => {
              const name = topic.sectionName ?? t.topicFallback;
              return (
                <li key={topic.topicKey} className="prep-topic">
                  <span className="prep-topic-text">
                    <strong>
                      <TopicName topic={topic} />
                    </strong>
                    <Because topic={topic} />
                  </span>
                  <span className="status-pill prep-tier">{t.tier[topic.tier]}</span>
                  <span className="prep-move">
                    <button type="button" aria-label={t.moveUp(name)} disabled={index === 0 || reorder.isPending} onClick={() => move(index, -1)}>
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={t.moveDown(name)}
                      disabled={index === plan.topics.length - 1 || reorder.isPending}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        </>
      ) : null}

      {plan.withoutProblems.length > 0 ? (
        <>
          <p>{t.withoutHeading}</p>
          <ul className="prep-topics">
            {plan.withoutProblems.map((topic) => (
              <li key={topic.topicKey} className="prep-topic">
                <span className="prep-topic-text">
                  <strong>
                    <TopicName topic={topic} />
                  </strong>
                  <Because topic={topic} />
                </span>
                <span className="status-pill prep-tier">{t.tier[topic.tier]}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {reorder.isError ? <p role="alert">{t.failed}</p> : null}
      {unavailable !== null ? <p role="status">{t.unavailable[unavailable]}</p> : null}
      {start.isError ? <FailureNotice error={start.error} onRetry={() => start.mutate()} /> : null}
    </section>
  );
}

export function PrepPage(): React.JSX.Element {
  const t = usePrep();
  const api = useApi();
  const { locale } = useLocale();
  const { focused } = useCourseFocus();
  const kinds = useAssessmentLabels();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(null);
  const [created, setCreated] = useState<Readonly<Record<string, string>>>({});

  const assessments = useQuery({ queryKey: queryKeys.examPrepAssessments, queryFn: () => api.getExamPrepAssessments() });
  const shown = (assessments.data?.assessments ?? []).filter((entry) => focused !== null && entry.assessment.courseRef === focused.moodleCourseId);
  const current = shown.find((entry) => entry.assessment.assessmentId === chosen) ?? shown[0];
  const planId = current === undefined ? null : (created[current.assessment.assessmentId] ?? current.planId);

  const plan = useQuery({
    queryKey: queryKeys.examPrepPlan(planId ?? ''),
    queryFn: () => api.getExamPrepPlan(planId as string),
    enabled: planId !== null,
  });
  const make = useMutation({
    mutationFn: (assessmentId: string) => api.createExamPrepPlan(crypto.randomUUID(), { assessmentId }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKeys.examPrepPlan(response.planId), response);
      setCreated((previous) => ({ ...previous, [response.assessment.assessmentId]: response.planId }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.examPrepAssessments });
    },
  });

  const courseTitle = focused === null ? '' : titleOf(focused, locale);
  const empty = focused !== null && assessments.isSuccess && shown.length === 0;
  if (empty) {
    return (
      <div className="page prep prep-empty">
        <span className="prep-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
            <path d="M3.5 10h17M8 3v4M16 3v4" />
          </svg>
        </span>
        <h1>{t.heading}</h1>
        <p>{t.noAssessments}</p>
        <Link className="prep-pill" to="/settings/courses#assessments">
          {t.addAssessment}
        </Link>
      </div>
    );
  }

  return (
    <div className="page prep">
      <div className="prep-head">
        <h1>{t.heading}</h1>
        {focused !== null && assessments.isSuccess ? (
          <Link className="prep-link" to="/settings/courses#assessments">
            {t.addAssessment}
          </Link>
        ) : null}
      </div>
      {focused === null ? <p>{t.noCourse}</p> : null}
      {assessments.isError ? <FailureNotice error={assessments.error} onRetry={() => void assessments.refetch()} /> : null}
      {assessments.isPending && focused !== null ? <p>{t.loading}</p> : null}
      {shown.length > 1 ? (
        <ul className="prep-assessment-choice" aria-label={t.pickAssessment}>
          {shown.map((entry) => (
            <li key={entry.assessment.assessmentId}>
              <button
                type="button"
                aria-pressed={entry.assessment.assessmentId === current?.assessment.assessmentId}
                onClick={() => setChosen(entry.assessment.assessmentId)}
              >
                <UntrustedText text={entry.assessment.title} /> · {kinds.kind(entry.assessment.kind)} {assessmentDate(entry.assessment.date, locale)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {current !== undefined && planId === null ? (
        <section className="prep-plan prep-hero" aria-labelledby="prep-make-title">
          <h2 id="prep-make-title">
            {courseTitle} · {kinds.kind(current.assessment.kind)}, {assessmentDate(current.assessment.date, locale)}
          </h2>
          <button type="button" className="prep-pill prep-hero-action" disabled={make.isPending} onClick={() => make.mutate(current.assessment.assessmentId)}>
            {t.makePlan}
          </button>
        </section>
      ) : null}
      {make.isError ? (
        make.error instanceof ApiRequestError ? (
          <p role="alert">{t.failed}</p>
        ) : (
          <FailureNotice error={make.error} />
        )
      ) : null}
      {plan.isError ? <FailureNotice error={plan.error} onRetry={() => void plan.refetch()} /> : null}
      {plan.data !== undefined ? <PlanView plan={plan.data} /> : null}
    </div>
  );
}
